import { useTranslation } from "react-i18next";
import { Package } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useStorageLocations, type StorageBinRow } from "@/hooks/useStorageBins";

export interface BinLocationChoice {
  /** Bins with the scanned code, one per storage location. */
  bins: StorageBinRow[];
  onPick: (bin: StorageBinRow) => void;
}

/**
 * Asks which storage location a scanned bin is in, when an old label's code
 * exists in more than one location (FIX-319). Nothing happens until the
 * person picks one.
 */
export function BinLocationChoiceDialog({
  choice,
  onClose,
}: {
  choice: BinLocationChoice | null;
  onClose: () => void;
}) {
  const { t } = useTranslation("inventory");
  const { data: locations = [] } = useStorageLocations();
  const locName = (id: string) => locations.find((l) => l.id === id)?.name ?? "—";
  const code = choice?.bins[0]?.address ?? "";

  return (
    <Dialog open={!!choice} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("binChoice.title", { defaultValue: "Which storage location?" })}
          </DialogTitle>
          <DialogDescription>
            {t("binChoice.description", {
              code,
              defaultValue: "Bin {{code}} exists in more than one storage location. Pick the one this label belongs to.",
            })}
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-1">
          {choice?.bins.map((b) => (
            <li key={b.id}>
              <button
                type="button"
                onClick={() => { onClose(); choice.onPick(b); }}
                className="w-full flex items-center gap-3 rounded-md border border-border px-3 py-2.5 hover:bg-muted transition-colors text-left"
              >
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary/10 text-primary font-mono text-sm shrink-0">
                  <Package className="w-3.5 h-3.5" />
                  {b.address}
                </span>
                <span className="text-sm text-foreground truncate">{locName(b.location_id)}</span>
              </button>
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">
          {t("binChoice.reprintHint", {
            defaultValue: "Reprint this bin's label to scan it directly next time.",
          })}
        </p>
      </DialogContent>
    </Dialog>
  );
}
